import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { clearWidgetFilter } from "../../../state/features/filters/actions";
import { HeaderSection } from "./headerSection";
import { DashboardStatusContainer } from "./dashboardStatusContainer";
import { DashboardWidgetsGrid } from "./dashboardWidgetsGrid";
import { DashboardWidgetsStatusContainer } from "./dashboardWidgetsStatusContainer";
import { useParams } from "react-router-dom";
import type { RootState } from "../../../state/store";
import { useDashboardDataContext } from "../../../helpers/hooks/useDashboardDataContext";

const formatIdList = (values: Array<number | string> | undefined | null) => {
  if (!values || values.length === 0) return "all";
  return values.map(String).join("|");
};

const formatDateRange = (
  dates: [string, string] | string[] | undefined | null
) => {
  if (!dates || dates.length < 2) return "all";
  const [start, end] = dates;
  if (!start || !end) return "all";
  return `${start}..${end}`;
};

// Per-widget dropdown filters live in state.filters.widgets as
// { key: string, data: { id, name, value }[] }[]. Extract any active staff
// selections so the dashboard root can advertise them as `data-filter-staff`.
const formatStaffFilter = (widgetFilters: any[] | undefined | null) => {
  if (!Array.isArray(widgetFilters) || widgetFilters.length === 0) return "all";

  const staffEntries = widgetFilters.filter(
    (entry) => typeof entry?.key === "string" && entry.key.toLowerCase().includes("staff")
  );

  if (staffEntries.length === 0) return "all";

  const ids = staffEntries.flatMap((entry) =>
    Array.isArray(entry?.data)
      ? entry.data.map((row: any) => row?.id).filter((v: any) => v != null)
      : []
  );

  return ids.length === 0 ? "all" : ids.map(String).join("|");
};

const DashboardContainer = () => {
  const dispatch = useDispatch();
  const { company_details } = useSelector((state: RootState) => state.company);
  const isMaintenance = company_details?.maintenance_mode?.enabled;
  const filters = useSelector((state: RootState) => state.filters);

  const {
    dashboardWidgets,
    dashboardInfo,
    isDashboardDataLoading,
    dashboardDataLoadError,
    refreshDashboardData,
    shouldEnableDashboardFetch,
    dashboardLockedStatus
  } = useDashboardDataContext();

  useEffect(() => {
    dispatch(clearWidgetFilter());
    shouldEnableDashboardFetch(true);
  }, []);

  const dashboardName = useMemo(() => {
    if (dashboardInfo) {
      return dashboardInfo.name;
    }
    return null;
  }, [dashboardInfo]);

  return (
    <LayoutWrapper
      top={isMaintenance ? 60 : 0}
      data-dashboard-id={dashboardInfo?.identifier}
      data-filter-location={formatIdList(filters?.locations as number[])}
      data-filter-staff={formatStaffFilter(filters?.widgets)}
      data-filter-date-range={formatDateRange(filters?.dates as [string, string])}
    >
      <DashboardStatusContainer
        key={dashboardName}
        shouldLoadDashboard={Boolean(dashboardInfo)}
        dashboardAuthError={dashboardLockedStatus}
      >
        <HeaderSection
          dashboardDisplayName={dashboardInfo?.display_name}
          refreshDashboard={refreshDashboardData}
        />
        <DashboardWidgetsStatusContainer
          hasData={Boolean(dashboardWidgets.length)}
          loadError={!dashboardLockedStatus.isEnabled && dashboardDataLoadError}
          loading={isDashboardDataLoading}
          refreshDashboard={refreshDashboardData}
        >
          <DashboardWidgetsGrid dashboard={dashboardName} dashboardWidgets={dashboardWidgets} />
        </DashboardWidgetsStatusContainer>
      </DashboardStatusContainer>
    </LayoutWrapper>
  );
};

const DashboardLayout = () => {
  const routeMatch = useParams();
  return <DashboardContainer key={routeMatch?.dashboard} />;
};

export default DashboardLayout;

const LayoutWrapper = styled.div`
  padding-top: ${(props: { top: number }) => props.top}px;
  padding-bottom: 200px;
  .grid-layout {
    top: 160px;
    margin: 20px;
    .react-grid-item.react-grid-placeholder {
      background: grey;
      border-radius: 20px;
    }
  }
`;
